import type { HazardIdentification } from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  NO_CATEGORY,
  NO_LIFECYCLE,
  NO_LOCATION,
  type HazardListFilters,
} from './hazardListFilters'
import { hazardHasNoMachineLocation } from './getHazardMachineLocationLabel'

function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim() === ''
}

function matchesAdequacy(
  hazard: HazardIdentification,
  filters: HazardListFilters
): boolean {
  if (filters.adequacy === 'all') {
    return true
  }
  if (filters.adequacy === 'not_adequately_reduced') {
    return hazard.riskAdequatelyReducedAt == null
  }
  return hazard.riskAdequatelyReducedAt != null
}

function matchesCategory(
  hazard: HazardIdentification,
  filters: HazardListFilters
): boolean {
  if (filters.category === 'all') {
    return true
  }
  if (filters.category === NO_CATEGORY) {
    return isBlank(hazard.type)
  }
  return hazard.type === filters.category
}

function matchesLocation(
  hazard: HazardIdentification,
  filters: HazardListFilters
): boolean {
  if (filters.location === 'all') {
    return true
  }
  if (filters.location === NO_LOCATION) {
    return hazardHasNoMachineLocation(hazard)
  }
  return hazard.machineLocationId === filters.location
}

function hazardHasNoEhsr(hazard: HazardIdentification): boolean {
  return hazard.ehsrClauseIds == null || hazard.ehsrClauseIds.length === 0
}

function matchesEhsr(
  hazard: HazardIdentification,
  filters: HazardListFilters
): boolean {
  const hasClauseFilter = filters.ehsrClauseIds.length > 0
  const hasNoEhsrFilter = filters.includeNoEhsr
  if (!hasClauseFilter && !hasNoEhsrFilter) {
    return true
  }

  // Clause IDs and "no EHSR" combine with OR within this dimension.
  let matches = false
  if (hasNoEhsrFilter && hazardHasNoEhsr(hazard)) {
    matches = true
  }
  if (hasClauseFilter) {
    const clauseIds = hazard.ehsrClauseIds ?? []
    const hasSelectedClause = filters.ehsrClauseIds.some((clauseId) =>
      clauseIds.includes(clauseId)
    )
    if (hasSelectedClause) {
      matches = true
    }
  }
  return matches
}

function hazardHasNoLifecycle(hazard: HazardIdentification): boolean {
  return hazard.lifecycles.length === 0
}

function matchesLifecycle(
  hazard: HazardIdentification,
  filters: HazardListFilters
): boolean {
  if (filters.lifecycles.length === 0) {
    return true
  }

  const selectedLifecycles = filters.lifecycles.filter(
    (lifecycle) => lifecycle !== NO_LIFECYCLE
  )
  const includesNoLifecycle = filters.lifecycles.includes(NO_LIFECYCLE)

  if (includesNoLifecycle && hazardHasNoLifecycle(hazard)) {
    return true
  }
  if (selectedLifecycles.length > 0) {
    return hazard.lifecycles.some((lifecycle) =>
      selectedLifecycles.includes(lifecycle)
    )
  }
  return false
}

function matchesSource(
  hazard: HazardIdentification,
  filters: HazardListFilters
): boolean {
  if (filters.source === 'all') {
    return true
  }
  return hazard.source === filters.source
}

export function filterHazards(
  hazards: HazardIdentification[],
  filters: HazardListFilters
): HazardIdentification[] {
  return hazards.filter(
    (hazard) =>
      matchesAdequacy(hazard, filters) &&
      matchesCategory(hazard, filters) &&
      matchesLocation(hazard, filters) &&
      matchesEhsr(hazard, filters) &&
      matchesLifecycle(hazard, filters) &&
      matchesSource(hazard, filters)
  )
}
